import React, { useState } from 'react';
import {
  Users,
  Award,
  ShieldCheck,
  Cpu,
  Globe,
  Star,
  Zap,
  CheckCircle2,
  Flame,
  TrendingUp,
  Play,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Stats from '../components/Stats';

const AboutUs = () => {
  const [activeEpisode, setActiveEpisode] = useState(0);

  const trendingTech = [
    {
      rank: '1',
      title: 'AI & Generative Neural Pipelines',
      category: 'ENTERPRISE ORIGINAL',
      match: '99% Match',
      rating: '18+',
      tags: ['RAG Vector DB', 'LLM Fine-Tuning', 'PyTorch'],
      desc: 'Bespoke AI chatbots, document indexing, and predictive machine learning models built for high-throughput enterprise scale.'
    },
    {
      rank: '2',
      title: 'Next.js & Custom WordPress CMS',
      category: 'POPULAR NOW',
      match: '98% Match',
      rating: 'All Ages',
      tags: ['Sub-Second Load', 'ACF Custom Blocks', 'React 19'],
      desc: 'High-velocity web architecture engineered for Google PageSpeed 98+ scores and seamless content editing.'
    },
    {
      rank: '3',
      title: 'Integrated Hospital & NCD Management',
      category: 'AWARD WINNER',
      match: '99% Match',
      rating: 'HIPAA Certified',
      tags: ['HL7 / FHIR v4', 'NCD Field Sync', 'LIMS Telemetry'],
      desc: 'HIPAA-compliant hospital ERP, OPD/IPD queue, and Non-Communicable Disease screening mobile app systems.'
    },
    {
      rank: '4',
      title: 'Automated CI/CD & Cloud Infrastructure',
      category: 'TRENDING #4',
      match: '97% Match',
      rating: 'ISO 27001',
      tags: ['Kubernetes', 'Terraform', 'AWS Serverless'],
      desc: 'Zero-downtime deployment pipelines with automated canary releases, Infrastructure as Code, and 99.99% uptime.'
    }
  ];

  const episodes = [
    {
      num: 'E01',
      title: 'Our Mission: High-Yield Software Craftsmanship',
      duration: '45 mins read',
      badge: 'Core Philosophy',
      desc: 'To build high-performing, resilient, and intuitive software applications that solve complex business challenges, boost operational efficiency, and deliver measurable ROI to our global enterprise partners.',
      highlights: ['Zero-Defect Code Architecture', 'Human-Centered UI/UX Engineering', 'Measurable Business ROI']
    },
    {
      num: 'E02',
      title: 'Our Vision: Global Benchmark in AI & Web Tech',
      duration: '60 mins read',
      badge: 'Future Roadmap',
      desc: 'To be recognized worldwide as a benchmark for technology craftsmanship, combining AI intelligence, human-centered design, and cloud scalability across every digital product we engineer.',
      highlights: ['AI-Native Ecosystems', 'Global Multi-Region Cloud Scaling', 'Enterprise Security Hardening']
    },
    {
      num: 'E03',
      title: 'Our Quality Guarantee: Continuous QA Telemetry',
      duration: 'SLA Guarantee',
      badge: 'Quality Assurance',
      desc: 'Rigorous multi-stage automated testing, peer code reviews, load testing under peak traffic, and continuous security compliance checks before every single release.',
      highlights: ['Automated End-to-End Testing', '24/7 Infrastructure Telemetry', 'Zero-Downtime Migration']
    }
  ];

  const coreValues = [
    {
      num: '01',
      title: 'Uncompromising Quality',
      tag: 'MUST WATCH',
      icon: ShieldCheck,
      color: 'text-rose-500',
      desc: 'Rigorous automated QA, security compliance, and peer code reviews for zero-defect software delivery.'
    },
    {
      num: '02',
      title: 'Transparent Partnership',
      tag: 'FAN FAVORITE',
      icon: Users,
      color: 'text-orange-400',
      desc: 'Open communications, weekly sprint demos, direct developer contact, and zero hidden costs.'
    },
    {
      num: '03',
      title: 'Continuous Innovation',
      tag: 'NEW SEASON',
      icon: Award,
      color: 'text-amber-400',
      desc: 'Pioneering modern tech stacks like Next.js, AI LLMs, and cloud-native serverless architectures.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-16 text-gray-100 font-['Roboto',sans-serif]">

      {/* Netflix-Style Billboard Hero Banner */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden border-b border-gray-800">

        {/* Background Graphic Poster Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/netflix_style_tech_banner.png"
            alt="Netflix Style Engineering Billboard"
            className="w-full h-full object-cover scale-105 filter brightness-75"
          />
          {/* Cinema Dark Vignette & Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/70 to-gray-950/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/90 to-transparent" />
        </div>

        <div className="container mx-auto px-6 relative z-10 py-16">
          <div className="max-w-3xl">

            {/* Netflix Original Badge Pill */}
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-red-600 to-orange-600 text-white px-3.5 py-1 rounded-md text-sm font-black uppercase tracking-widest mb-6 shadow-lg shadow-red-600/30">
              <Flame className="w-4 h-4 fill-white" />
              <span>HOAPS ORIGINAL • #1 IN ENTERPRISE TECH</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight drop-shadow-2xl">
              Driving Digital Transformation Through <span className="bg-gradient-to-r from-red-500 via-orange-400 to-amber-300 bg-clip-text text-transparent">Engineering Excellence</span>
            </h1>

            {/* Netflix Metadata Line */}
            <div className="flex flex-wrap items-center gap-4 text-sm md:text-sm font-semibold text-gray-300 mb-6 font-mono">
              <span className="text-emerald-400 font-bold flex items-center gap-1">
                <Star className="w-4 h-4 fill-emerald-400 text-emerald-400" />
                99% Match for Enterprise Scale
              </span>
              <span className="bg-gray-800 border border-gray-700 px-2 py-0.5 rounded text-gray-200">
                2026 EDITION
              </span>
              <span className="bg-red-500/20 border border-red-500/40 text-red-400 px-2 py-0.5 rounded font-bold">
                ULTRA HD 4K SPEED
              </span>
              <span className="text-gray-400">HIPAA & ISO Certified</span>
            </div>

            {/* Subtitle Copy */}
            <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-8 max-w-2xl font-normal drop-shadow">
              We are a full-service software product engineering agency empowering global enterprises and ambitious startups to innovate, scale, and lead with modern AI, cloud, and web architectures.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4">
              <a href="/contact">
                <Button className="bg-white hover:bg-gray-200 text-black px-8 py-6 text-base font-extrabold rounded-xl shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-3">
                  <Play className="w-5 h-5 fill-black" />
                  Start Transformation Demo
                </Button>
              </a>
              <a href="#trending-rail">
                <Button variant="outline" className="bg-gray-900/80 hover:bg-gray-800 border-gray-700 text-white px-8 py-6 text-base font-bold rounded-xl backdrop-blur-md flex items-center gap-2">
                  <Info className="w-5 h-5 text-gray-400" />
                  Explore Core Stack
                </Button>
              </a>
            </div>

          </div>
        </div>

      </section>

      {/* Netflix Row 1: "Trending Now: Core Engineering Solutions" */}
      <section id="trending-rail" className="py-16 bg-slate-50 border-t border-b border-gray-200">
        <div className="container mx-auto px-6">

          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-6 h-6 text-red-600" />
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
                Trending Now in Tech Engineering
              </h2>
            </div>
            <span className="text-sm font-['Roboto',sans-serif] font-bold text-gray-500 uppercase tracking-wider hidden md:block">
              SWIPE TO EXPLORE ➔
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trendingTech.map((item, idx) => (
              <div
                key={idx}
                className="group relative bg-white border border-gray-200 hover:border-red-500/70 rounded-2xl p-6 transition-all duration-500 hover:scale-[1.03] shadow-md hover:shadow-xl hover:shadow-red-500/10 flex flex-col justify-between overflow-hidden text-left"
              >
                {/* Large Rank Number Watermark */}
                <span className="absolute -bottom-6 -right-2 text-7xl font-black text-gray-200 group-hover:text-red-500/10 font-mono transition-colors pointer-events-none select-none">
                  #{item.rank}
                </span>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[11px] font-black uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 px-2.5 py-1 rounded-md">
                      {item.category}
                    </span>
                    <span className="text-sm font-bold font-['Roboto',sans-serif] text-emerald-600">
                      {item.match}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    {item.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-200 space-y-2 relative z-10">
                  <div className="flex flex-wrap gap-1.5">
                    {item.tags.map((tag, tIdx) => (
                      <span key={tIdx} className="text-xs font-['Roboto',sans-serif] font-medium text-gray-700 bg-slate-100 border border-gray-300 px-2.5 py-1 rounded-md shadow-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Netflix Episode Feature: Mission, Vision & Quality Guarantee */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-b border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-12">
            <span className="text-sm font-mono font-bold text-red-500 uppercase tracking-widest block mb-2">
              EPISODIC OVERVIEW
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
              Season 1: Foundation & Vision
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Select an episode to explore our mission, vision, and quality guarantee.
            </p>

            {/* Episode Selector Tabs */}
            <div className="inline-flex p-1.5 bg-gray-900 border border-gray-800 rounded-2xl gap-2 mt-6">
              {episodes.map((ep, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveEpisode(idx)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-bold font-['Roboto',sans-serif] transition-all ${activeEpisode === idx
                    ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white'
                    }`}
                >
                  {ep.num}: {ep.badge}
                </button>
              ))}
            </div>
          </div>

          {/* Active Episode Spotlight Card (ONLY BOX IS WHITE) */}
          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-6">
              <div className="flex items-center space-x-3">
                <span className="text-2xl font-black font-['Roboto',sans-serif] text-red-600">{episodes[activeEpisode].num}</span>
                <h3 className="text-xl font-bold text-gray-900">{episodes[activeEpisode].title}</h3>
              </div>
              <span className="text-sm font-['Roboto',sans-serif] font-medium text-gray-700 bg-gray-100 border border-gray-200 px-3 py-1 rounded-full">
                {episodes[activeEpisode].duration}
              </span>
            </div>

            <p className="text-gray-700 text-base leading-relaxed mb-8">
              {episodes[activeEpisode].desc}
            </p>

            <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              {episodes[activeEpisode].highlights.map((item, hIdx) => (
                <div key={hIdx} className="bg-slate-50 p-4 rounded-xl border border-gray-200 flex items-center space-x-3">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-800">{item}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Stats Showcase */}
      <Stats />

      {/* Netflix Row 2: "Top 10 Core Values" */}
      <section className="py-20 bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">

          <div className="text-center mb-16">
            <span className="text-sm font-mono text-orange-400 font-bold uppercase tracking-widest block mb-2">
              OUR GUIDING PRINCIPLES
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Top Core Values Driving Every Project
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              The fundamental engineering tenets that dictate every line of code, sprint demo, and client deployment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((val, idx) => (
              <div
                key={idx}
                className="bg-gray-900/80 border border-gray-800 hover:border-orange-500/60 rounded-3xl p-8 transition-all duration-300 hover:scale-[1.02] shadow-xl flex flex-col justify-between relative group"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-3xl font-black font-mono text-orange-400">{val.num}</span>
                    <span className="text-[11px] font-bold font-mono uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-full">
                      {val.tag}
                    </span>
                  </div>

                  <val.icon className={`w-10 h-10 ${val.color} mb-4 group-hover:scale-110 transition-transform`} />

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-300 transition-colors">
                    {val.title}
                  </h3>

                  <p className="text-sm text-gray-300 leading-relaxed mb-4">
                    {val.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Leadership & Engineering Team Section */}
      <section className="py-20 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-cyan-400 font-bold uppercase tracking-widest block mb-2">
              MEET OUR EXPERTS
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Leadership & Engineering Team
            </h2>
            <p className="text-gray-300 text-base max-w-2xl mx-auto">
              Our multidisciplinary team of cloud architects, senior full-stack developers, AI engineers, and UI/UX designers powering global digital transformations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: 'Software Architecture Team',
                role: 'Full-Stack & Cloud Systems',
                desc: 'Specialized in microservices, high-throughput APIs, Node.js, and serverless infrastructure.',
                icon: Cpu,
                color: 'from-cyan-500 to-blue-600'
              },
              {
                name: 'UI/UX Design Studio',
                role: 'Product Design & Design Systems',
                desc: 'Crafting responsive, accessible, and high-converting user interfaces and Figma design systems.',
                icon: Zap,
                color: 'from-amber-500 to-orange-600'
              },
              {
                name: 'AI & Data Science Hub',
                role: 'LLM & Predictive Intelligence',
                desc: 'Building autonomous AI agents, RAG vector indexing, and fine-tuned machine learning models.',
                icon: Flame,
                color: 'from-rose-500 to-red-600'
              },
              {
                name: 'QA & Security Guild',
                role: 'Continuous Testing & DevOps',
                desc: 'Ensuring zero-defect releases with automated E2E testing, penetration testing, and ISO/HIPAA compliance.',
                icon: ShieldCheck,
                color: 'from-emerald-500 to-teal-600'
              }
            ].map((member, idx) => (
              <div
                key={idx}
                className="bg-gray-950 border border-gray-800 rounded-3xl p-6 hover:border-cyan-500/50 transition-all duration-300 shadow-xl text-left group"
              >
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${member.color} flex items-center justify-center mb-6 shadow-lg`}>
                  <member.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                  {member.name}
                </h3>
                <p className="text-xs font-mono text-cyan-400 font-semibold mb-3">
                  {member.role}
                </p>
                <p className="text-xs text-gray-300 leading-relaxed">
                  {member.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-Width Executive Consultation CTA */}
      <section className="py-24 bg-slate-50 relative overflow-hidden border-t border-gray-200 w-full">
        <div className="container mx-auto px-6 relative z-10 text-center">
          <div className="bg-white border border-gray-200 rounded-3xl p-10 md:p-16 shadow-xl w-full">
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Ready to Watch Your Business Scale with Custom Software?
            </h2>
            <p className="text-gray-600 text-base md:text-lg mb-8 max-w-4xl mx-auto">
              Schedule a technical session with our software architects to plan custom web app engineering, mobile app development, or enterprise AI integration.
            </p>
            <a href="/contact">
              <Button className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:from-red-700 hover:to-amber-600 text-white px-8 py-6 text-base font-extrabold rounded-xl shadow-xl">
                Schedule Engineering Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;
